import React, { FC, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Flex,
  Icon,
  Relative,
  Scrollbar,
  Text,
  useDateFormat,
} from '@revolut-internal/ui-kit'

import { ChatMessage } from 'components'
import { useAssistantChatMessagesContext } from 'pages/AssistantPlayground/hooks'
import { groupMessagesByDate } from 'lib/utils/messages'
import { useAssistantChatMessagesFeedback } from 'pages/AssistantPlayground/hooks/useAssistantChatMessagesFeedback'

import { ChatMessagesListWrapper } from './ChatMessagesList.styles'
import { AssistantChatMessageGenerating } from '../AssistantChatMessageGenerating'

interface ChatMessagesListProps {
  isGeneratingAssistantResponse: boolean
}

export const ChatMessagesList: FC<ChatMessagesListProps> = ({
  isGeneratingAssistantResponse,
}) => {
  const {
    chatMessages,
    memoryResetActive,
    getChatMessageFeedbackById,
    chatMessagesFeedbackMap,
    setChatMessagesFeedbackMap,
    removeAssistantChatMessageFeedbackById,
  } = useAssistantChatMessagesContext()

  const {
    refetchChatMessageFeedbackById,
    isLoading,
    handleOnChatMessageFeedbackVisible,
  } = useAssistantChatMessagesFeedback({
    chatMessages,
    chatMessagesFeedbackMap,
    setChatMessagesFeedbackMap,
    getChatMessageFeedbackById,
    removeAssistantChatMessageFeedbackById,
  })

  const dateFormat = useDateFormat()

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const messageScrollRef = useRef<HTMLDivElement | null>(null)

  // Always scroll to the bottom of the chat whenever the messages update
  useEffect(() => {
    messageScrollRef.current?.scrollIntoView()
  }, [chatMessages])

  const groupedMessages = useMemo(
    () => groupMessagesByDate(chatMessages, dateFormat),
    [chatMessages, dateFormat],
  )

  if (chatMessages.length === 0) {
    return (
      <Text variant="heading3" mb="s-32">
        Hey! What can I help with?
      </Text>
    )
  }

  return (
    <Relative overflow="hidden" height="100%">
      <ChatMessagesListWrapper px="s-16" ref={scrollRef} space="s-16">
        {Object.entries(groupedMessages).map(([groupKey, group]) => (
          <React.Fragment key={groupKey}>
            <Text variant="body3" textAlign="center" color="grey-50">
              {groupKey}
            </Text>
            {group.map((message, messageIndex) => (
              <ChatMessage
                // eslint-disable-next-line react/no-array-index-key
                key={`msg-${message.messageId}-${message.authorRole}-${messageIndex}`}
                message={message}
                onAssistantMessageVisible={handleOnChatMessageFeedbackVisible}
                containerRef={scrollRef}
                getChatMessageFeedback={getChatMessageFeedbackById}
                refetchChatMessageFeedback={refetchChatMessageFeedbackById}
                isFeedbackLoading={isLoading}
              />
            ))}
          </React.Fragment>
        ))}
        {isGeneratingAssistantResponse && <AssistantChatMessageGenerating />}
        {memoryResetActive && (
          <Flex alignItems="center" justifyContent="center" gap="s-8">
            <Icon name="Database" size="s-16" color="grey-50" />
            <Text variant="body3" textAlign="center" color="grey-50">
              Memory reset
            </Text>
          </Flex>
        )}
        <Box ref={messageScrollRef} height="s-16" style={{ flexShrink: 0 }} />
      </ChatMessagesListWrapper>
      <Scrollbar scrollRef={scrollRef} />
    </Relative>
  )
}
